import React from 'react';
import { Box, Stack } from '@mui/material';

import { Breakdown, CommonCard } from '@organisms';
import { DateTime } from 'luxon';
import { TextView } from '@atoms';
import { useBroadcastEvents, useLeadEventStats } from '@api';
import TokenOutlinedIcon from '@mui/icons-material/TokenOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';

interface TotalTokenCusumeProps {
  analyticsToken?: TokenCounts;
  start: DateTime | null;
  end: DateTime | null;
  billingAccount: BillingAccount | undefined;
}

const num = (v: number | undefined | null) => Number(v ?? 0);

const TotalTokenCusume: React.FC<TotalTokenCusumeProps> = ({
  analyticsToken,
  start,
  end,
  billingAccount
}: TotalTokenCusumeProps) => {
  const { data: eventsStats } = useLeadEventStats({
    type: 'all',
    startDate: start?.toISO({ suppressMilliseconds: true, includeOffset: false }),
    endDate: end?.toISO({ suppressMilliseconds: true, includeOffset: false }),
    locationId: billingAccount?.locationId
  });

  const { data: boardcastEvents } = useBroadcastEvents({
    startDate: start?.toISO({ suppressMilliseconds: true, includeOffset: false }),
    endDate: end?.toISO({ suppressMilliseconds: true, includeOffset: false }),
    type: 'all',
    locationId: billingAccount?.locationId
  });

  const hasTotalToken = analyticsToken?.totalTokenConsumption;
  const hasAi = analyticsToken?.totalAiCount;
  const hasReapit = analyticsToken?.reapitCount;
  const hasWaba = analyticsToken?.whatsAppTotalCount;
  const hasTwilio = analyticsToken?.totalTwillioCount;
  const hasLeadEvents = !!eventsStats?.data?.total;
  const hasBroadcast = !!boardcastEvents?.data?.total;

  return (
    <Box sx={{ mt: 2, mb: 3 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(6, 1fr)'
          },
          columnGap: 2,
          rowGap: 3,
          alignItems: 'stretch'
        }}
      >
        {hasTotalToken && (
          <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 1', md: 'span 2' } }}>
            <CommonCard
              title="Total Token Consumption"
              count={num(analyticsToken?.totalTokenConsumption)}
              icon={<TokenOutlinedIcon />}
              accent="neutral"
            />
          </Box>
        )}

        {hasAi && (
          <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 1', md: 'span 2' } }}>
            <CommonCard
              title="Total AI Usage"
              count={num(analyticsToken?.totalAiCount)}
              icon={<SmartToyOutlinedIcon />}
              accent="info"
            />
          </Box>
        )}

        {hasReapit && (
          <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2', md: 'span 2' } }}>
            <CommonCard
              title="Total Negotiator Usage"
              count={num(analyticsToken?.reapitCount)}
              icon={<ChatBubbleOutlineOutlinedIcon />}
              accent="secondary"
            />
          </Box>
        )}

        {hasWaba && (
          <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2', md: 'span 3' } }}>
            <Stack spacing={0.5}>
              <CommonCard
                title="WABA"
                count={num(analyticsToken?.whatsAppTotalCount)}
                icon={<WhatsAppIcon />}
                accent="info"
              >
                <Breakdown
                  groups={[
                    {
                      heading: 'By type',
                      items: [
                        { label: 'Template', value: num(analyticsToken?.templateCount) },
                        { label: 'Inbound', value: num(analyticsToken?.inboundCount) },
                        { label: 'Outbound', value: num(analyticsToken?.outboundCount) },
                        ...(num(analyticsToken?.whatsAppUnreconciledCount) > 0
                          ? [{ label: 'Unreconciled', value: num(analyticsToken?.whatsAppUnreconciledCount) }]
                          : [])
                      ]
                    },
                    {
                      heading: 'By category',
                      items: [
                        { label: 'Sales', value: num(analyticsToken?.salesCount) },
                        { label: 'Lettings', value: num(analyticsToken?.lettingsCount) },
                        { label: 'Mortgage', value: num(analyticsToken?.mortgageCount) },
                        { label: 'Valuation', value: num(analyticsToken?.valuationCount) },
                        { label: 'Other', value: num(analyticsToken?.otherCount) }
                      ]
                    }
                  ]}
                />
              </CommonCard>
              <TextView $fontSize={10} $fontColor={'rgba(15, 23, 42, 0.5)'} align={'right'}>
                * Above numbers is based on WhatsApp SRT Usage
              </TextView>
            </Stack>
          </Box>
        )}

        {hasLeadEvents && (
          <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2', md: 'span 3' } }}>
            <Stack spacing={0.5}>
              <CommonCard
                title="Lead Events"
                count={num(eventsStats?.data?.total)}
                icon={<TrackChangesOutlinedIcon />}
                accent="highlight"
              >
                <Breakdown
                  groups={[
                    {
                      heading: `Qualified: ${num(eventsStats?.data?.qualified?.total)}`,
                      items: [
                        { label: 'Sales', value: num(eventsStats?.data?.qualified?.Sales) },
                        { label: 'Lettings', value: num(eventsStats?.data?.qualified?.Lettings) },
                        { label: 'Other', value: num(eventsStats?.data?.qualified?.unknown) }
                      ]
                    },
                    {
                      heading: `Valuation: ${num(eventsStats?.data?.valuation?.total)}`,
                      items: [
                        { label: 'Sales', value: num(eventsStats?.data?.valuation?.Sales) },
                        { label: 'Lettings', value: num(eventsStats?.data?.valuation?.Lettings) },
                        { label: 'Other', value: num(eventsStats?.data?.valuation?.unknown) }
                      ]
                    },
                    {
                      heading: `Mortgage: ${num(eventsStats?.data?.mortgage?.total)}`,
                      items: [
                        { label: 'Sales', value: num(eventsStats?.data?.mortgage?.Sales) },
                        { label: 'Lettings', value: num(eventsStats?.data?.mortgage?.Lettings) },
                        { label: 'Other', value: num(eventsStats?.data?.mortgage?.unknown) }
                      ]
                    }
                  ]}
                />
              </CommonCard>
              <TextView $fontSize={10} $fontColor={'rgba(15, 23, 42, 0.5)'} align={'right'}>
                * Above numbers is based on WhatsApp SRT Usage
              </TextView>
            </Stack>
          </Box>
        )}

        {hasTwilio && (
          <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2', md: 'span 3' } }}>
            <CommonCard
              title="Total Twillio Usage"
              count={num(analyticsToken?.totalTwillioCount)}
              icon={<PhoneInTalkOutlinedIcon />}
              accent="warning"
            />
          </Box>
        )}

        {hasBroadcast && (
          <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2', md: 'span 3' } }}>
            <CommonCard
              title="Broadcast Events"
              count={num(boardcastEvents?.data?.total)}
              icon={<CampaignOutlinedIcon />}
              accent="info"
            >
              <Breakdown
                groups={[
                  {
                    heading: `SMS: ${num(boardcastEvents?.data?.sms?.total)}`,
                    items: [
                      { label: 'Sales', value: num(boardcastEvents?.data?.sms?.Sales) },
                      { label: 'Lettings', value: num(boardcastEvents?.data?.sms?.Lettings) },
                      { label: 'Other', value: num(boardcastEvents?.data?.sms?.unknown) }
                    ]
                  },
                  {
                    heading: `Whatsapp: ${num(boardcastEvents?.data?.whatsapp?.total)}`,
                    items: [
                      { label: 'Sales', value: num(boardcastEvents?.data?.whatsapp?.Sales) },
                      { label: 'Lettings', value: num(boardcastEvents?.data?.whatsapp?.Lettings) },
                      { label: 'Other', value: num(boardcastEvents?.data?.whatsapp?.unknown) }
                    ]
                  }
                ]}
              />
            </CommonCard>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TotalTokenCusume;
